import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
  Button,
  DialogFooter,
  Form,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';
import { dashboardT } from '../../../../../lib/messages';
import {
  CampaignStatus,
  resolveCampaignStatusLabel,
  type Campaign,
  type CampaignPayload,
} from '../../model';
export function CampaignForm({
  campaign,
  parentBrand,
  brandOptions,
  brandsLoading,
  pending,
  saveDisabled = false,
  onCancel,
  onSubmit,
}: {
  campaign: Campaign | null;
  parentBrand?: { id: string; name: string } | undefined;
  brandOptions: Array<{ id: string; name: string }>;
  brandsLoading: boolean;
  pending: boolean;
  saveDisabled?: boolean;
  onCancel: () => void;
  onSubmit: (payload: CampaignPayload) => Promise<void>;
}) {
  const methods = useForm({
    defaultValues: {
      name: campaign?.name ?? '',
      brandId: campaign?.brand?.id ?? campaign?.brandId ?? parentBrand?.id ?? '',
      status: resolveFormStatus(campaign?.status),
    },
  });
  const form = useWatch({ control: methods.control });
  const [submitted, setSubmitted] = React.useState(false);
  const nameId = React.useId();
  const brandId = React.useId();
  const statusId = React.useId();
  const valid = Boolean(form.name?.trim() && form.brandId);
  let primaryLabel = dashboardT('campaigns.create');
  if (campaign) primaryLabel = dashboardT('campaigns.save');
  if (pending) primaryLabel = dashboardT('campaigns.saving');

  return (
    <Form {...methods}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid && !pending && !saveDisabled) {
            void methods.handleSubmit((values) =>
              onSubmit({ ...values, name: values.name.trim() }),
            )();
          }
        }}
      >
        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold" htmlFor={nameId}>
              {dashboardT('campaigns.name')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id={nameId}
              className="h-9"
              required
              maxLength={255}
              value={form.name}
              placeholder={dashboardT('campaigns.namePlaceholder')}
              onChange={(event) => {
                methods.setValue('name', event.target.value, { shouldDirty: true });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold" htmlFor={brandId}>
              {dashboardT('campaigns.brand')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={form.brandId ?? ''}
              disabled={brandsLoading || Boolean(campaign || parentBrand)}
              onValueChange={(value) => {
                methods.setValue('brandId', value, { shouldDirty: true });
              }}
            >
              <SelectTrigger id={brandId} className="h-9">
                <SelectValue placeholder={dashboardT('campaigns.selectBrand')} />
              </SelectTrigger>
              <SelectContent>
                {brandOptions.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold" htmlFor={statusId}>
              {dashboardT('campaigns.status')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={form.status ?? CampaignStatus.Active}
              onValueChange={(value) => {
                methods.setValue('status', resolveFormStatus(value), { shouldDirty: true });
              }}
            >
              <SelectTrigger id={statusId} className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[CampaignStatus.Active, CampaignStatus.Paused, CampaignStatus.Archived].map(
                  (status) => (
                    <SelectItem key={status} value={status}>
                      {resolveCampaignStatusLabel(status)}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
          {submitted && !valid ? (
            <p role="alert" className="text-destructive text-sm">
              {dashboardT('campaigns.required')}
            </p>
          ) : null}
        </div>
        <DialogFooter className="border-border border-t px-5 py-3">
          <Button type="button" variant="outline" disabled={pending} onClick={onCancel}>
            {dashboardT('campaigns.cancel')}
          </Button>
          <Button type="submit" disabled={pending || saveDisabled}>
            {primaryLabel}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

function resolveFormStatus(value: string | undefined): CampaignStatus {
  switch (value) {
    case CampaignStatus.Active:
    case CampaignStatus.Paused:
    case CampaignStatus.Archived:
    case CampaignStatus.Draft:
    case CampaignStatus.Completed:
      return value;
    default:
      return CampaignStatus.Active;
  }
}
